import {
  alpha,
  Card,
  CircularProgress,
  Theme,
  Typography,
} from "@material-ui/core";
import { from } from "manipula";
import { Fragment, memo, useEffect, useMemo, useState } from "react";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  AxleStatusDto,
  MaintenanceDashboardApiFactory,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { Widget } from "../widgets/Widget";
import { VehicleWidgetProps } from "./VehicleWidgetProps";
import {
  mdiThermometer,
  mdiGauge,
  mdiGaugeEmpty,
  mdiTruckCheckOutline,
  mdiTruckAlertOutline,
  mdiTransitConnectionHorizontal,
} from "@mdi/js";
import Icon from "@mdi/react";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import resourceNames from "../../../../common/data/resourceNames";
import { useHistory } from "react-router-dom";
import { Empty } from "../widgets/Empty";

interface Props extends VehicleWidgetProps {}
interface TireCellProps {
  iconPath: string;
  title: string;
  value: string | number;
  iconClassName?: string;
  className?: string;
  onClick?: () => void;
}

const useStyles = makeStyles<Theme>((theme) => ({
  warning: {
    color: theme.palette.warning.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  info: {
    color: theme.palette.info.main,
  },
  clickable: {
    cursor: "pointer",
    "&:hover": {
      background: alpha(
        theme.palette.action.hover,
        theme.palette.action.hoverOpacity
      ),
    },
  },
}));

const TireCell = memo(
  ({
    iconPath,
    title,
    value,
    iconClassName,
    className,
    onClick,
  }: TireCellProps) => {
    return (
      <div className={classNames("flex-v", className)} onClick={onClick}>
        <Icon
          path={iconPath}
          title={title}
          size={1}
          className={iconClassName}
        />
        <Typography variant="caption">{title}</Typography>
        <div className="cell-value">
          <Typography variant="body2">{value}</Typography>
        </div>
      </div>
    );
  }
);

export const TpmsInfoWidget = ({ vehicle, ...props }: Props) => {
  const axios = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<AxleStatusDto[]>([]);
  const classes = useStyles();
  const history = useHistory();

  useEffect(() => {
    if (!axios) {
      setLoading(false);
      return;
    }

    if (!vehicle?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMaintenancedashboardTirestatusesVehicleIdGet(vehicle.id)
      .then((result) => {
        setData(result.data);
      })
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [axios, vehicle]);

  const cols = useMemo(() => {
    if (!data || !data.length) {
      return "";
    }
    const queryable = from(data);
    return `tpms-table table-${queryable.max(
      (x) => x.tires?.length ?? 0
    )}-cols`;
  }, [data]);

  return (
    <Widget {...props}>
      <div className="categories tmps-info">
        {loading ? (
          <div className="category">
            <CircularProgress size="3rem" />
          </div>
        ) : data && data.length ? (
          <div className={cols + ' tmps-info-table'}>
            {data.map((axle, axleIdx) => (
              <Fragment key={axle.axle}>
                <div className="table-row-header">
                  <Icon
                    path={mdiTransitConnectionHorizontal}
                    title="Axle"
                    size={1}
                    className={classes.info}
                  />
                  <Typography variant="body1">
                    {`Axle ${axleIdx + 1}`}
                  </Typography>
                </div>
                {axle.tires?.map((tire, idx) => (
                  <Card
                    key={tire.tire}
                    className={`table-col tmps-info-table-col table-col-${idx + 1} flex-v`}
                  >
                    <div className="cell-title">
                      <Typography variant="body1">
                        {`Tire ${idx + 1}`}
                      </Typography>
                    </div>
                    <div className="cell-contents">
                      <TireCell
                        iconPath={mdiThermometer}
                        title="Temperature"
                        value={
                          tire.temperature ? `${tire.temperature} °C` : "-"
                        }
                        iconClassName={classes.info}
                      />
                      <TireCell
                        iconPath={mdiGauge}
                        title="Pressure"
                        value={tire.psi ? `${tire.psi} PSI` : "-"}
                        iconClassName={classes.info}
                      />
                      <TireCell
                        iconPath={mdiGaugeEmpty}
                        title="Leakage"
                        iconClassName={
                          tire.airLeakageRate ? classes.error : classes.info
                        }
                        value={
                          tire.airLeakageRate ? `${tire.airLeakageRate}` : "-"
                        }
                      />
                      <TireCell
                        className={
                          tire.faults?.length ? classes.clickable : undefined
                        }
                        onClick={
                          tire.faults?.length
                            ? () =>
                                history.push(
                                  `/${resourceNames.ASSETS_VEHICLE_MAINTENANCE}/${vehicle.id}`
                                )
                            : undefined
                        }
                        iconPath={
                          tire.faults?.length
                            ? mdiTruckAlertOutline
                            : mdiTruckCheckOutline
                        }
                        title="Faults"
                        iconClassName={
                          tire.faults?.length ? classes.error : classes.info
                        }
                        value={tire.faults?.length ?? 0}
                      />
                    </div>
                  </Card>
                ))}
              </Fragment>
            ))}
          </div>
        ) : (
          <Empty />
        )}
      </div>
    </Widget>
  );
};
